import { find, remove } from 'lodash-es'
import { defineStore } from 'pinia'

import { useUsersStore } from 'src/stores/users.js'

import {
  addAccountsToList,
  createList,
  deleteList,
  fetchLists,
  getList,
  getListAccounts,
  removeAccountsFromList,
  updateList,
} from 'src/api/user.js'
import { pollingActions } from 'src/services/poller/poller.js'

export const useListsStore = defineStore('lists', {
  state: () => ({
    poller: null,
    allLists: [],
    allListsObject: {},
  }),
  getters: {
    findListTitle() {
      return (id) => {
        if (!this.allListsObject[id]) return
        return this.allListsObject[id].title
      }
    },
    findListExclusive() {
      return (id) => !!this.allListsObject[id]?.exclusive
    },
    // Pleroma's list emoji: { emoji, emojiUrl }, nulls when there is none
    findListEmoji() {
      return (id) => {
        const pleroma = this.allListsObject[id]?.pleroma
        return {
          emoji: pleroma?.emoji ?? null,
          emojiUrl: pleroma?.emoji_url ?? null,
        }
      }
    },
    // a server that knows list emoji says so in every list (null or not)
    listEmojiSupported: (state) =>
      state.allLists.some((list) => list.pleroma && 'emoji' in list.pleroma),
    findListAccounts() {
      return (id) => [...this.allListsObject[id].accountIds]
    },
  },
  actions: {
    // every four minutes
    ...pollingActions((store) => store.loadLists(), 240000),
    loadLists() {
      return fetchLists({}).then(({ data: lists }) => this.setLists(lists))
    },
    // the lists as the server has them; the poll brings no members, so
    // the known ones stay
    setLists(value) {
      this.allLists = value
      this.allListsObject = Object.fromEntries(
        value.map((list) => [
          list.id,
          {
            ...list,
            accountIds: this.allListsObject[list.id]?.accountIds ?? [],
            membersLoaded: !!this.allListsObject[list.id]?.membersLoaded,
          },
        ]),
      )
    },
    async createList({ title, exclusive, emoji }) {
      return await createList({
        title,
        exclusive,
        emoji,
      }).then(({ data: list }) => {
        this.rememberList({
          listId: list.id,
          title,
          exclusive: list.exclusive,
          pleroma: list.pleroma,
        })
        return list
      })
    },
    async fetchList({ listId }) {
      return await getList({
        listId,
      }).then(({ data: list }) =>
        this.rememberList({
          listId: list.id,
          title: list.title,
          exclusive: list.exclusive,
          pleroma: list.pleroma,
        }),
      )
    },
    // what the server says about a list, kept here without saving it back
    // (pleroma: the server's pleroma fields, with the emoji; left as they
    // were when the answer has none)
    rememberList({ listId, title, exclusive, pleroma }) {
      if (!this.allListsObject[listId]) {
        this.allListsObject[listId] = { accountIds: [] }
      }
      const known = this.allListsObject[listId]
      known.title = title
      known.exclusive = !!exclusive
      if (pleroma) known.pleroma = pleroma

      const entry = find(this.allLists, { id: listId })
      if (!entry) {
        this.allLists.push({
          id: listId,
          title,
          exclusive: !!exclusive,
          ...(pleroma && { pleroma }),
        })
      } else {
        entry.title = title
        entry.exclusive = !!exclusive
        if (pleroma) entry.pleroma = pleroma
      }
    },
    async fetchListAccounts({ listId }) {
      return await getListAccounts({
        listId,
      }).then((result) => {
        useUsersStore().addNewUsers(result)
        if (!this.allListsObject[listId]) {
          this.allListsObject[listId] = { accountIds: [] }
        }
        this.allListsObject[listId].accountIds = result.data.map(({ id }) => id)
        this.allListsObject[listId].membersLoaded = true
      })
    },
    async setList({ listId, title, exclusive, emoji }) {
      const { data } = await updateList({
        listId,
        title,
        exclusive,
        emoji,
      })
      this.rememberList({
        listId,
        title,
        exclusive: exclusive ?? this.allListsObject[listId]?.exclusive,
        pleroma: data?.pleroma,
      })
    },
    async setListAccounts({ listId, accountIds }) {
      const saved = this.allListsObject[listId]?.accountIds || []
      const added = accountIds.filter((id) => !saved.includes(id))
      const removed = saved.filter((id) => !accountIds.includes(id))
      if (!this.allListsObject[listId]) {
        this.allListsObject[listId] = { accountIds: [] }
      }
      this.allListsObject[listId].accountIds = accountIds
      const promises = []
      if (added.length > 0) {
        promises.push(
          addAccountsToList({
            listId,
            accountIds: added,
          }),
        )
      }
      if (removed.length > 0) {
        promises.push(
          removeAccountsFromList({
            listId,
            accountIds: removed,
          }),
        )
      }
      await Promise.all(promises)
    },
    async addListAccount({ listId, accountId }) {
      return await addAccountsToList({
        listId,
        accountIds: [accountId],
      }).then((result) => {
        if (!this.allListsObject[listId]) {
          this.allListsObject[listId] = { accountIds: [] }
        }
        this.allListsObject[listId].accountIds.push(accountId)
        return result
      })
    },
    async removeListAccount({ listId, accountId }) {
      return await removeAccountsFromList({
        listId,
        accountIds: [accountId],
      }).then((result) => {
        if (!this.allListsObject[listId]) {
          this.allListsObject[listId] = { accountIds: [] }
        }
        const { accountIds } = this.allListsObject[listId]
        const set = new Set(accountIds)
        set.delete(accountId)
        this.allListsObject[listId].accountIds = [...set]

        return result
      })
    },
    async deleteList({ listId }) {
      await deleteList({
        listId,
      })

      delete this.allListsObject[listId]
      remove(this.allLists, (list) => list.id === listId)
    },
  },
})
